import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import LanguageToggle from "@/components/LanguageToggle";
import { supabase } from "@/integrations/supabase/client";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { User, LogOut, ChevronRight, Scale, Droplets, Heart, Ruler, Loader2, Pencil, Check, X, UserCog, FileText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";

const ProfilePage = () => {
  const { t, language } = useLanguage();
  const { user, logout } = useAuth();
  const bn = language === "bn";
  const navigate = useNavigate();
  const { toast } = useToast();
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<"weight" | "height" | "blood_sugar" | null>(null);
  const [editValue, setEditValue] = useState("");

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      const { data } = await supabase.from("profiles").select("*").eq("user_id", user.id).single();
      setProfile(data);
      setLoading(false);
    };
    load();
  }, [user]);

  const weight = profile?.weight ?? 0;
  const height = profile?.height ?? 0;
  const bloodSugar = profile?.blood_sugar ?? 0;
  const bmi = height > 0 ? (weight / ((height / 100) ** 2)).toFixed(1) : "—";

  const startEdit = (field: "weight" | "height" | "blood_sugar") => {
    setEditing(field);
    setEditValue(String(profile?.[field] ?? ""));
  };

  const saveEdit = async () => {
    if (!editing || !profile || !user) return;
    const val = parseFloat(editValue);
    if (isNaN(val) || val <= 0) return;
    const previousProfile = profile;
    const { error } = await supabase.from("profiles").update({ [editing]: val }).eq("user_id", user.id);
    if (error) {
      toast({ title: bn ? "আপডেট হয়নি" : "Update failed", description: error.message, variant: "destructive" });
      return;
    }
    setProfile({ ...previousProfile, [editing]: val });
    setEditing(null);
    setEditValue("");
  };

  const cancelEdit = () => { setEditing(null); setEditValue(""); };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fbfaf7] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const topStats = [
    { key: "age", label: bn ? "বয়স" : "Age", value: profile?.age ? `${profile.age}` : "—", unit: bn ? "বছর" : "yrs" },
    { key: "gender", label: bn ? "লিঙ্গ" : "Gender", value: profile?.gender ?? "—", unit: "" },
  ];

  const healthStats = [
    { key: "height" as const, icon: Ruler, label: bn ? "উচ্চতা" : "Height", value: height ? `${height}` : "—", unit: "cm", color: "gradient-mint", editable: true },
    { key: "weight" as const, icon: Scale, label: bn ? "ওজন" : "Weight", value: weight ? `${weight}` : "—", unit: "kg", color: "gradient-primary", editable: true },
    { key: "blood_sugar" as const, icon: Droplets, label: bn ? "রক্তের শর্করা" : "Blood Sugar", value: bloodSugar ? `${bloodSugar}` : "—", unit: "mg/dL", color: "gradient-accent", editable: true },
    { key: "bmi" as const, icon: Heart, label: "BMI", value: bmi, unit: "", color: "gradient-warm", editable: false },
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const menuItems = [
    { icon: UserCog, label: "Edit Profile", labelBn: "প্রোফাইল সম্পাদনা", path: "/settings", onClick: undefined },
    { icon: FileText, label: "Health Records", labelBn: "স্বাস্থ্য রেকর্ড", path: "/patient-health-records", onClick: undefined },
    { icon: LogOut, label: "Logout", labelBn: "লগআউট", path: "/login", onClick: handleLogout },
  ];

  return (
    <div className="min-h-screen bg-[#fbfaf7] pb-28 text-[#263449]">
      <div className="relative min-h-[225px] overflow-hidden px-6 pb-5 pt-10">
        <div className="relative z-10 flex items-center justify-between">
          <h1 className="font-serif text-[28px] font-extrabold leading-none text-[#18544e]">{t("nav.profile")}</h1>
          <LanguageToggle />
        </div>
        <div className="relative z-10 mt-7 flex items-center gap-4">
          <div className="flex h-[76px] w-[76px] shrink-0 items-center justify-center rounded-full bg-[#e4f1ee]">
            <User className="h-9 w-9 text-[#167b73]" strokeWidth={1.7} />
          </div>
          <div className="min-w-0 max-w-[185px]">
            <h2 className="break-words font-serif text-[18px] font-extrabold leading-tight text-[#18544e]">{profile?.full_name || (bn ? "রোগী" : "Patient")}</h2>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
              {topStats.map((s) => (
                <span key={s.key} className="whitespace-nowrap text-xs font-medium text-[#697273]">
                  {s.label}: {s.value}{s.unit ? ` ${s.unit}` : ""}
                </span>
              ))}
            </div>
          </div>
        </div>
        <img src="/profile-health.png" alt="" aria-hidden="true" className="absolute right-[-4px] top-[64px] z-0 h-[130px] w-[175px] object-contain" />
      </div>

      <div className="px-5">
        <div className="grid grid-cols-2 gap-x-5 gap-y-7 rounded-[1.7rem] border border-[#e7e8e5] bg-white px-5 py-6">
          {healthStats.map((s) => (
            <motion.div key={s.label} className="relative flex min-w-0 items-start gap-3" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              {editing === s.key ? (
                <div className="flex w-full flex-col items-start gap-1.5">
                  <Input type="number" value={editValue} onChange={(e) => setEditValue(e.target.value)} className="h-8 w-24 bg-background text-sm" autoFocus onKeyDown={(e) => e.key === "Enter" && saveEdit()} />
                  <div className="flex gap-1">
                    <button onClick={saveEdit} className="p-1 rounded-lg bg-success/20"><Check className="h-3 w-3 text-success" /></button>
                    <button onClick={cancelEdit} className="p-1 rounded-lg bg-destructive/20"><X className="h-3 w-3 text-destructive" /></button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#e4f1ee]">
                    <s.icon className="h-6 w-6 text-[#167b73]" strokeWidth={1.6} />
                  </div>
                  <div className="min-w-0 flex-1 pt-1">
                    <div className="flex items-center justify-between gap-1 pr-1">
                      <p className="whitespace-nowrap text-base font-bold text-[#263449]">{s.value}{s.unit ? ` ${s.unit}` : ""}</p>
                      {s.editable && (
                        <button onClick={() => startEdit(s.key as "weight" | "height" | "blood_sugar")} className="shrink-0 p-0.5">
                          <Pencil className="h-4 w-4 text-[#697273]" strokeWidth={1.6} />
                        </button>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-[#697273]">{s.label}</p>
                  </div>
                </>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {profile?.health_profile && (
        <div className="px-5 mt-4">
          <div className="flex items-center justify-between rounded-[1.7rem] border border-[#e7e8e5] bg-white px-5 py-5">
            <div>
              <p className="text-sm font-bold uppercase tracking-wide text-[#167b73]">{bn ? "স্বাস্থ্য প্রোফাইল" : "Health Profile"}</p>
              <p className="mt-2 text-base font-medium text-[#263449]">{profile.health_profile}</p>
            </div>
            <ChevronRight className="h-5 w-5 text-[#697273]" strokeWidth={1.7} />
          </div>
        </div>
      )}

      <div className="mt-4 space-y-2 px-5">
        {menuItems.map((item) => (
          <button key={item.label} onClick={() => item.onClick ? item.onClick() : navigate(item.path)} className="flex w-full items-center gap-3 rounded-[1.4rem] border border-[#e7e8e5] bg-white px-4 py-3 text-left">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#e4f1ee]">
              <item.icon className="h-5 w-5 text-[#167b73]" strokeWidth={1.7} />
            </div>
            <span className="flex-1 text-sm font-bold text-[#263449]">{bn ? item.labelBn : item.label}</span>
            <ChevronRight className="h-5 w-5 text-[#697273]" strokeWidth={1.7} />
          </button>
        ))}
      </div>
    </div>
  );
};

export default ProfilePage;
